import React, { ReactNode } from "react";
import {
  mdiCheckboxBlankCircleOutline,
  mdiCheckboxMarkedCircleOutline,
} from "@mdi/js";
import { format, isValid } from "date-fns";
import { cn } from "../../../styles";
import { NULL_DATETIME_PATTERN } from "../../../constants";
import { formatModes, parseISOString } from "../../../helper";
import { useDataTableContext } from "../context";
import { DataTableColumn, DataTableRow, ColumnType } from "../types";
import { Icon } from "../../../atoms";
import { FrozenCellInfo } from "../hooks/useFrozenColumns";
import { frozenCellStyle, frozenCellClassName } from "./frozenStyle";

type DateColumnType =
  | ColumnType.DATE
  | ColumnType.LOCALDATE
  | ColumnType.DATETIME
  | ColumnType.TIME;

const DATE_FORMATS: Record<DateColumnType, string> = {
  [ColumnType.DATE]: "dd.MM.yyyy",
  [ColumnType.LOCALDATE]: "dd.MM.yyyy",
  [ColumnType.DATETIME]: "dd.MM.yyyy, HH:mm",
  [ColumnType.TIME]: "HH:mm",
};

function isNullDatetimeIso(value: any): boolean {
  return typeof value === "string" && !!value.match(NULL_DATETIME_PATTERN);
}

function toDate(value: any, type: DateColumnType): Date | null {
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value !== "string") return null;

  // DATE has no timezone meaning, so keep the calendar day exactly as sent
  const parsed =
    type === ColumnType.DATE
      ? (parseISOString(value, formatModes.FIXED_DAY) ?? parseISOString(value))
      : parseISOString(value);

  return parsed instanceof Date && isValid(parsed) ? parsed : null;
}

export interface CellProps<TRow> {
  column: DataTableColumn<TRow>;
  row: DataTableRow<TRow>;
  className?: string;
  children?: ReactNode;
  freezeInfo?: FrozenCellInfo;
  freezeBackgroundClassName?: string;
}

function formatValue(value: any, type: ColumnType): React.ReactNode {
  if (value == null) return "";

  switch (type) {
    case ColumnType.BOOL:
      return value ? (
        <Icon path={mdiCheckboxMarkedCircleOutline} />
      ) : (
        <Icon path={mdiCheckboxBlankCircleOutline} />
      );
    case ColumnType.DATE:
    case ColumnType.LOCALDATE:
    case ColumnType.DATETIME:
    case ColumnType.TIME: {
      if (isNullDatetimeIso(value)) return "";
      const date = toDate(value, type);
      return date ? format(date, DATE_FORMATS[type]) : String(value);
    }
    case ColumnType.INTEGER:
      return Number.isInteger(value) ? value.toLocaleString() : String(value);
    case ColumnType.FLOAT:
      return typeof value === "number" ? value.toLocaleString() : String(value);
    default:
      return String(value);
  }
}

export function Cell<TRow>({
  children,
  column,
  row,
  className,
  freezeInfo,
  freezeBackgroundClassName,
}: CellProps<TRow>) {
  const { dense, cellClasses } = useDataTableContext<TRow>();

  const value = (row as any)[column.id];
  const customClasses = cellClasses?.(column, row as TRow);

  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[column.align];

  return (
    <td
      className={cn(
        dense ? "px-1.5 py-0.5 text-xs" : "px-3 py-2 text-sm",
        alignClass,
        column.fitContent && "w-0 whitespace-nowrap",
        freezeInfo && freezeBackgroundClassName,
        frozenCellClassName(freezeInfo),
        customClasses,
        className,
      )}
      style={{ width: column.width, ...frozenCellStyle(freezeInfo) }}
    >
      {column.render?.(row as TRow, column) ??
        children ??
        formatValue(value, column.type)}
    </td>
  );
}
